import { Injectable, OnDestroy } from '@angular/core'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import {
	PostMarketHours,
	PreMarketHours,
	PreMarketMinutes,
} from '@modules/alpaca-module/market-status/constants/exchange-work-times'
import { BehaviorSubject, Subject } from 'rxjs'
import { MarketClockInterface } from '@modules/alpaca-module/market-status/interfaces/market-clock.interface'
import { MarketStatusPeriodsType } from '@modules/alpaca-module/market-status/types/market-status-periods.type'
import { OneDayInMs } from '@shared/constants/times/one-day-in-ms'
import { MarketStatusInterface } from '@modules/alpaca-module/market-status/interfaces/market-status.interface'
import { MarketPeriodInterface } from '@modules/alpaca-module/market-status/interfaces/market-period.interface'
import { MinutesInHour } from '@shared/constants/times/minutes-in-hour'

@Injectable({
	providedIn: 'root',
})
export class MarketStatusHelperService implements OnDestroy {
	protected readonly map: MapConstructor = Map
	protected readonly date: DateConstructor = Date
	protected readonly math: Math = Math
	protected readonly oneDayInMs: number = OneDayInMs
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly preMarketHours: number = PreMarketHours
	protected readonly preMarketMinutes: number = PreMarketMinutes
	protected readonly postMarketHours: number = PostMarketHours
	protected readonly minutesInHour: number = MinutesInHour

	public marketStatus$: BehaviorSubject<MarketStatusInterface | null> =
		new BehaviorSubject<MarketStatusInterface | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	get marketStatus(): MarketStatusEnum {
		const marketStatus: MarketStatusInterface | null = this.marketStatus$.getValue()
		return marketStatus?.status ?? this.marketStatusEnum.marketClose
	}

	get isMarketOpen(): boolean {
		return this.marketStatus$.getValue()?.status === this.marketStatusEnum.marketOpen
	}

	private _getMarketStatus(marketClock: MarketClockInterface): MarketStatusEnum {
		const currentDateTime: Date = marketClock.timestamp
		const nextOpenDate: Date = new this.date(marketClock.next_open)
		const nextCloseDate: Date = new this.date(marketClock.next_close)
		const todayClose: Date =
			nextOpenDate > currentDateTime
				? new this.date(
						nextCloseDate.getTime() -
							this.math.ceil((nextOpenDate.getTime() - currentDateTime.getTime()) / this.oneDayInMs) *
								this.oneDayInMs,
					)
				: nextCloseDate
		const postMarketFinish: Date = new this.date(todayClose)
		postMarketFinish.setHours(postMarketFinish.getHours() + this.postMarketHours)

		if (currentDateTime >= this._getStartPreMarket(marketClock) && currentDateTime < nextOpenDate) {
			return this.marketStatusEnum.preMarket
		} else if (marketClock.is_open || (currentDateTime >= nextOpenDate && currentDateTime < nextCloseDate)) {
			return this.marketStatusEnum.marketOpen
		} else if (currentDateTime >= todayClose && currentDateTime <= postMarketFinish) {
			return this.marketStatusEnum.postMarket
		} else {
			return this.marketStatusEnum.marketClose
		}
	}

	private _getMarketStatusPeriods(marketClock: MarketClockInterface): MarketStatusPeriodsType {
		const serverDate: Date = marketClock.timestamp
		const nextOpenDate: Date = new this.date(marketClock.next_open)
		const nextCloseDate: Date = new this.date(marketClock.next_close)

		const todayOpen: Date = marketClock.is_open
			? this._getTodayOpen(marketClock)
			: new this.date(marketClock.next_open)

		const startPreMarket: Date = new this.date(todayOpen)
		startPreMarket.setHours(startPreMarket.getHours() - this.preMarketHours)
		startPreMarket.setMinutes(startPreMarket.getMinutes() - this.preMarketMinutes)

		const todayClose: Date =
			!marketClock.is_open && nextOpenDate > serverDate
				? new this.date(
						nextCloseDate.getTime() -
							this.math.ceil((nextOpenDate.getTime() - serverDate.getTime()) / this.oneDayInMs) *
								this.oneDayInMs,
					)
				: nextCloseDate
		const todayPostMarketEnd: Date = new this.date(todayClose)
		todayPostMarketEnd.setHours(todayPostMarketEnd.getHours() + this.postMarketHours)

		return new this.map([
			[this.marketStatusEnum.marketClose, { start: todayPostMarketEnd, end: startPreMarket }],
			[this.marketStatusEnum.preMarket, { start: startPreMarket, end: todayOpen }],
			[this.marketStatusEnum.marketOpen, { start: todayOpen, end: nextCloseDate }],
			[this.marketStatusEnum.postMarket, { start: todayClose, end: todayPostMarketEnd }],
		])
	}

	private _getTodayOpen(marketClock: MarketClockInterface): Date {
		const nextOpenDate: Date = new this.date(marketClock.next_open)
		const nextCloseDate: Date = new this.date(marketClock.next_close)
		const todayOpen: Date = new this.date(nextCloseDate)
		todayOpen.setHours(nextOpenDate.getHours(), nextOpenDate.getMinutes(), 0, 0)
		todayOpen.setDate(nextOpenDate.getDate() - 1)
		return todayOpen
	}

	private _getStartPreMarket(marketClock: MarketClockInterface): Date {
		const nextOpenDate: Date = new this.date(marketClock.next_open)
		nextOpenDate.setHours(nextOpenDate.getHours() - this.preMarketHours)
		nextOpenDate.setMinutes(nextOpenDate.getMinutes() - this.preMarketMinutes)
		return nextOpenDate
	}

	private _getTimezone(time: Date): string {
		const offsetMinutes: number = time.getTimezoneOffset()
		const hours: number = -offsetMinutes / this.minutesInHour

		return `${hours >= 0 ? '+' : ''}${hours} GMT`
	}

	public updateMarketStatus(marketClock: MarketClockInterface): void {
		const marketStatus: MarketStatusInterface | null = this.marketStatus$.getValue()
		const status: MarketStatusEnum = this._getMarketStatus(marketClock)
		const marketPeriod: MarketStatusPeriodsType = this._getMarketStatusPeriods(marketClock)
		const timezone: string = this._getTimezone(marketClock.timestamp)

		this.marketStatus$.next({
			...marketStatus,
			status,
			period: marketPeriod,
			time: marketClock.timestamp,
			timezone,
		})
	}

	public getTimePeriodDate(
		marketStatus: MarketStatusInterface,
		status: MarketStatusEnum,
		type: 'start' | 'end',
	): Date | null {
		const periods: MarketPeriodInterface | undefined = marketStatus.period.get(status)
		if (!periods) return null

		return type === 'start' ? periods.start : periods.end
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
